import { onAuthStateChanged, type User } from "firebase/auth";
import { getDocFromServer, onSnapshot, type Transaction } from "firebase/firestore";
import { auth } from "@/lib/firebase-auth";
import { paths } from "@/data/firestore/paths";
import {
  currentManagerUid,
  hasManagementProtection,
  isActiveManager,
  isManagementEligible,
} from "@/domain/membership";
import type { DevelopmentIdentity } from "./development-identity";
import { useSystemStore } from "@/stores/systemStore";

export type DashboardAccessMode = "read-only" | "manage";
export type CustomerAccess = {
  adminView?: boolean;
  mode: DashboardAccessMode;
  userId: string | null;
  pending?: boolean;
  canRead?: boolean;
};

function isCurrentSystemAdmin(user: Pick<User, "uid"> | null): boolean {
  const state = useSystemStore.getState();
  return Boolean(
    user && state.uid === user.uid && state.adminAccessResolved && state.isSystemAdmin,
  );
}

export function deriveCustomerAccess(
  user: (Pick<User, "uid"> & DevelopmentIdentity) | null,
  account: unknown,
  member: unknown,
  systemAdmin = false,
  customerId?: string,
): CustomerAccess {
  const userId = user && !user.isAnonymous ? user.uid : null;
  const customerExists = typeof account === "object" && account !== null;
  const membership = Boolean(
    userId &&
    currentManagerUid(account) === userId &&
    isActiveManager(member) &&
    (member as Record<string, unknown>)["uid"] === userId,
  );
  const privileged = customerExists && systemAdmin;
  return {
    mode:
      userId && (privileged || (membership && isManagementEligible(account)))
        ? "manage"
        : "read-only",
    userId,
    canRead:
      customerExists &&
      (privileged || membership || customerId === "demo" || !hasManagementProtection(account)),
  };
}

export async function getCustomerAccess(
  customerId: string,
  transaction?: Transaction,
): Promise<CustomerAccess> {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user || user.isAnonymous) return { mode: "read-only", userId: null, canRead: false };
  try {
    const read = (ref: ReturnType<typeof paths.customer>) =>
      transaction ? transaction.get(ref) : getDocFromServer(ref);
    const [account, member] = await Promise.all([
      read(paths.customer(customerId)),
      read(paths.member(customerId, user.uid)),
    ]);
    if (auth.currentUser?.uid !== user.uid)
      return { mode: "read-only", userId: null, canRead: false };
    return deriveCustomerAccess(
      user,
      account.data(),
      member?.data(),
      isCurrentSystemAdmin(user),
      customerId,
    );
  } catch {
    return { mode: "read-only", userId: user.uid, canRead: false };
  }
}

export async function requireManageAccess(
  customerId: string,
  transaction?: Transaction,
): Promise<{ userId: string }> {
  const access = await getCustomerAccess(customerId, transaction);
  if (access.mode !== "manage" || !access.userId) {
    throw new Error(
      "Sign in with an active membership for an eligible company before making changes.",
    );
  }
  return { userId: access.userId };
}

export function watchCustomerAccess(
  customerId: string,
  observer: (access: CustomerAccess) => void,
): () => void {
  let generation = 0;
  let stopDocuments = () => {};
  let stopSystem = () => {};
  const stopAuth = onAuthStateChanged(auth, (user) => {
    const current = ++generation;
    stopDocuments();
    stopSystem();
    observer({ ...deriveCustomerAccess(user, null, null), pending: true });
    let account: unknown;
    let member: unknown;
    let accountReady = false;
    let memberReady = !user || user.isAnonymous;
    let previous: string | undefined;
    const emit = () => {
      if (current !== generation) return;
      const access = {
        ...deriveCustomerAccess(user, account, member, isCurrentSystemAdmin(user), customerId),
        pending: !accountReady || !memberReady,
      };
      const signature = JSON.stringify(access);
      if (signature !== previous) {
        previous = signature;
        observer(access);
      }
    };
    stopSystem = useSystemStore.subscribe(emit);
    const failAccount = () => {
      account = null;
      accountReady = true;
      emit();
    };
    const failMember = () => {
      member = null;
      memberReady = true;
      emit();
    };
    const subscriptions = [
      onSnapshot(
        paths.customer(customerId),
        { includeMetadataChanges: true },
        (snapshot) => {
          if (
            current !== generation ||
            snapshot.metadata?.fromCache ||
            snapshot.metadata?.hasPendingWrites
          )
            return;
          account = snapshot.data();
          accountReady = true;
          emit();
        },
        failAccount,
      ),
      ...(!user || user.isAnonymous
        ? []
        : [
            onSnapshot(
              paths.member(customerId, user.uid),
              { includeMetadataChanges: true },
              (snapshot) => {
                if (
                  current !== generation ||
                  snapshot.metadata?.fromCache ||
                  snapshot.metadata?.hasPendingWrites
                )
                  return;
                member = snapshot.data();
                memberReady = true;
                emit();
              },
              failMember,
            ),
          ]),
    ];
    stopDocuments = () => subscriptions.forEach((stop) => stop());
  });
  return () => {
    generation++;
    stopAuth();
    stopDocuments();
    stopSystem();
  };
}

export function canViewOwnerOperations(customerId: string, access: CustomerAccess): boolean {
  // Read access for the routed Dashboard; mutations still require manage access.
  return Boolean(customerId) && !access.pending && access.canRead !== false;
}
